import React, { useEffect } from 'react'
import {
	EuiButtonIcon,
	EuiDescriptionList,
	EuiDescriptionListDescription,
	EuiDescriptionListTitle,
	EuiFlexGroup,
	EuiFlexItem,
	EuiSpacer,
	EuiText,
	EuiTitle,
	EuiToolTip,
} from '@equipmentshare/ds2'
import { FileCopyLineIcon } from '@equipmentshare/ds2-icons'
import { FullHeightPanel } from '../RequestDetails.styles'
import { TITLE_MARGIN } from '../RequestDetails.constants'
import { formatDateTime } from '../../../utils/formatters/date'
import ContactInfo from './ContactInfo'
import useBranchQuery from '../../../hooks/useBranchQuery'

type User = {
	id?: string,
	firstName?: string | null,
	lastName?: string | null,
	emailAddress?: string | null,
}

type Inventory = {
	name: string,
	branchId?: string | null,
}

type DeliveryAddress = {
	line1?: string | null,
	line2?: string | null,
	city?: string | null,
	state?: string | null,
	postalCode?: string | null,
}

type DemandRequest = {
	requestNumber: string,
	dateCreated: string,
	deliveryAddress?: DeliveryAddress | null,
	requestingInventory?: Inventory | null,
	requestedByUser?: User | null,
	contactPhonePrimary?: string | null,
	notes?: string | null,
	dateCancelled?: string | null,
	cancelledByUser?: User | null,
}

type RequestInfoPanelProps = {
	request: DemandRequest,
	onCopy: ( text: string, label: string ) => void,
}

/**
 * Component for displaying request information panel with contact details
 */
export default function RequestInfoPanel( { request, onCopy }: RequestInfoPanelProps ) {
	const { fetchBranch, branch } = useBranchQuery()

	// Fetch branch data when component mounts or branchId changes
	useEffect( () => {
		if ( request.requestingInventory?.branchId ) {
			fetchBranch( request.requestingInventory.branchId )
		}
	}, [ request.requestingInventory?.branchId, fetchBranch ] )

	// Check if the requester cancelled the request
	const requesterCancelled = request.dateCancelled &&
		request.cancelledByUser &&
		request.requestedByUser &&
		request.cancelledByUser.id === request.requestedByUser.id

	// Format the address for display
	const formatAddress = () => {
		if ( !branch?.location ) {
			return null
		}

		const location = branch.location
		const addressParts = []

		if ( location.street_1 ) {
			addressParts.push( location.street_1 )
		}
		if ( location.street_2 ) {
			addressParts.push( location.street_2 )
		}
		if ( location.city || location.state || location.zip_code ) {
			const cityStateZip = [
				location.city,
				location.state?.abbreviation,
				location.zip_code,
			].filter( Boolean ).join( ' ' )
			if ( cityStateZip ) {
				addressParts.push( cityStateZip )
			}
		}

		return addressParts.length > 0 ? addressParts : null
	}

	const addressLines = formatAddress()

	// An alternate delivery address entered on the request form, for a branch
	// whose Fleet address is not where it can accept deliveries. Formatted the
	// same way as the branch address above so the two read alike.
	const formatDeliveryAddress = () => {
		const address = request.deliveryAddress
		if ( !address ) {
			return null
		}

		const addressParts = []
		if ( address.line1 ) {
			addressParts.push( address.line1 )
		}
		if ( address.line2 ) {
			addressParts.push( address.line2 )
		}
		const cityStateZip = [ address.city, address.state, address.postalCode ].filter( Boolean ).join( ' ' )
		if ( cityStateZip ) {
			addressParts.push( cityStateZip )
		}

		return addressParts.length > 0 ? addressParts : null
	}

	const deliveryAddressLines = formatDeliveryAddress()

	const handleCopyDeliveryAddress = () => {
		if ( deliveryAddressLines ) {
			onCopy( deliveryAddressLines.join( '\n' ), 'Delivery address' )
		}
	}

	// Format the address as a string for copying
	const getAddressString = () => {
		if ( !addressLines || addressLines.length === 0 ) {
			return ''
		}
		return addressLines.join( '\n' )
	}

	const handleCopyAddress = () => {
		const addressString = getAddressString()
		if ( addressString ) {
			onCopy( addressString, 'Address' )
		}
	}

	return (
		<FullHeightPanel hasBorder>
			<EuiTitle size="s">
				<h4 style={TITLE_MARGIN}>REQUEST</h4>
			</EuiTitle>
			<EuiFlexGroup gutterSize="l" alignItems="flexStart">
				{/* Left side: Request info */}
				<EuiFlexItem>
					<EuiDescriptionList compressed>
						<EuiDescriptionListTitle>Request Number</EuiDescriptionListTitle>
						<EuiDescriptionListDescription>
							REQ-{request.requestNumber}
						</EuiDescriptionListDescription>
						<EuiDescriptionListTitle>
							Requested on
						</EuiDescriptionListTitle>
						<EuiDescriptionListDescription>
							{formatDateTime( request.dateCreated )}
						</EuiDescriptionListDescription>
						<EuiDescriptionListTitle>Requested From</EuiDescriptionListTitle>
						<EuiDescriptionListDescription>
							<div>
								{request.requestingInventory?.name ?? '—'}
								{addressLines && addressLines.length > 0 && (
									<EuiFlexGroup gutterSize="xs" alignItems="flexStart" responsive={false}>
										<EuiFlexItem grow={false}>
											<EuiText size="s" color="subdued" style={{ marginTop: '4px' }}>
												{addressLines.map( ( line ) => (
													<div key={line}>{line}</div>
												) )}
											</EuiText>
										</EuiFlexItem>
										<EuiFlexItem grow={false}>
											<EuiToolTip content="Copy address">
												<EuiButtonIcon
													iconType={FileCopyLineIcon}
													aria-label="Copy address"
													onClick={handleCopyAddress}
													size="xs"
													display="empty"
												/>
											</EuiToolTip>
										</EuiFlexItem>
									</EuiFlexGroup>
								)}
							</div>
						</EuiDescriptionListDescription>
						{deliveryAddressLines && (
							<>
								<EuiDescriptionListTitle>Deliver To</EuiDescriptionListTitle>
								<EuiDescriptionListDescription>
									<EuiFlexGroup gutterSize="xs" alignItems="flexStart" responsive={false}>
										<EuiFlexItem grow={false}>
											<EuiText size="s">
												{deliveryAddressLines.map( ( line ) => (
													<div key={line}>{line}</div>
												) )}
											</EuiText>
										</EuiFlexItem>
										<EuiFlexItem grow={false}>
											<EuiToolTip content="Copy delivery address">
												<EuiButtonIcon
													iconType={FileCopyLineIcon}
													aria-label="Copy delivery address"
													onClick={handleCopyDeliveryAddress}
													size="xs"
													display="empty"
												/>
											</EuiToolTip>
										</EuiFlexItem>
									</EuiFlexGroup>
								</EuiDescriptionListDescription>
							</>
						)}
						{requesterCancelled && request.dateCancelled && (
							<>
								<EuiDescriptionListTitle>Cancelled</EuiDescriptionListTitle>
								<EuiDescriptionListDescription>
									{formatDateTime( request.dateCancelled )}
									{request.cancelledByUser && (
										<> by {request.cancelledByUser.firstName} {request.cancelledByUser.lastName}</>
									)}
								</EuiDescriptionListDescription>
							</>
						)}
					</EuiDescriptionList>
				</EuiFlexItem>
				{/* Right side: Contact info */}
				<EuiFlexItem>
					<ContactInfo
						user={request.requestedByUser}
						phone={request.contactPhonePrimary}
						onCopy={onCopy}
					/>
				</EuiFlexItem>
			</EuiFlexGroup>
			{request.notes && (
				<>
					<EuiSpacer size="s" />
					<EuiDescriptionList compressed>
						<EuiDescriptionListTitle>Notes</EuiDescriptionListTitle>
						<EuiDescriptionListDescription>
							<EuiText size="s">{request.notes}</EuiText>
						</EuiDescriptionListDescription>
					</EuiDescriptionList>
				</>
			)}
		</FullHeightPanel>
	)
}
